import { useCallback, useState } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import type { WizardStepDef } from "./WizardStepper";

interface UseWizardNavOptions<T extends FieldValues> {
  form: UseFormReturn<T>;
  steps: WizardStepDef[];
  /**
   * Campos validados ao sair de cada etapa. **Precisa ser estável**
   * (`useCallback` ou função de módulo) — entra nas dependências do `goNext`.
   */
  getStepFields: (index: number) => Path<T>[];
}

/**
 * Navegação entre etapas de um wizard de aluguel.
 *
 * Só avança quando os campos da etapa atual passam na validação, e libera
 * o clique no stepper apenas para etapas já alcançadas — impedindo pular
 * validação. Compartilhado pelos wizards de criação do vendedor e do admin.
 */
export function useWizardNav<T extends FieldValues>({
  form,
  steps,
  getStepFields,
}: UseWizardNavOptions<T>) {
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const lastStep = steps.length - 1;

  const goNext = useCallback(async () => {
    const fields = getStepFields(step);
    const valid = fields.length === 0 ? true : await form.trigger(fields);
    if (!valid) return;
    const next = Math.min(step + 1, lastStep);
    setStep(next);
    setMaxReached((reached) => Math.max(reached, next));
  }, [step, lastStep, form, getStepFields]);

  const goBack = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);

  const goTo = useCallback(
    (index: number) => {
      if (index <= maxReached) setStep(index);
    },
    [maxReached],
  );

  return { step, maxReached, goNext, goBack, goTo };
}
